import { PublicOptions } from '@/components/import/public-section'
import Data, { treeNode } from '@/data'
import { ProfileImported, ProfileMetadata, ProfileType, UserEntry } from '@/types/api'
import { Item } from '@/types/item'
import { defaultPlayerClass, ItemId, ItemOrId, Profile } from '@/types/profile'
import { getUniqueId, isNumber, isString, keyIs, notNull } from '@maxroll-media-group/common-libs'
import _, { isArray, isObject } from 'lodash'
import Store, { defaultPlanner, StoreState } from 'store'
import { treeHistoryNodes, validateTreeEmbed } from './tree'
import { validateAuthor } from './validation'

export function selectItem(items: StoreState['items'], id: ItemOrId): Item | undefined {
  return typeof id === 'number' ? items[id] : id
}

export function stateGetItem(state: StoreState, id: ItemOrId): Item | undefined {
  return selectItem(state.items, id)
}

export function stateSetImported(state: StoreState, imported: ProfileImported) {
  return { ...state, id: Store.newId(), imported }
}

export function stateDeleteImports(state: StoreState, ...ids: string[]) {
  if (ids.includes(state.imported?.id!)) {
    state = { ...state }
    delete state.imported
  }
  return state
}

export function stateRemoveItem(state: StoreState, id: number) {
  if (!state.items[id]) return state
  state = { ...state }
  state.items = { ...state.items }
  delete state.items[id]
  state.embeds = state.embeds.map((embed) => {
    if ('items' in embed && Object.values(embed.items).includes(id)) {
      embed = { ...embed }
      const items: { [key in string]?: ItemId } = {}
      for (const [slot, item] of Object.entries(embed.items)) {
        if (item !== id) items[slot] = item
      }
      embed.items = items
    }
    return embed
  })
  return state
}

export function profileIsEmpty(profile: Profile) {
  //TODO: when adding more stuff to profile, update this logic
  if (
    profile.equipment.variants.some(
      (v) => Object.values(v.items).some(Boolean) || (v.priority && Object.values(v.priority).some(Boolean))
    )
  )
    return false
  if (profile.passives.variants.length !== 1 || profile.passives.variants[0].history.length) return false

  if (profile.atlas.variants.length !== 1 || profile.atlas.variants[0].history.length) return false

  if (profile.skills.steps.length !== 1 || profile.skills.steps[0].skills.length) return false

  if (profile.skillRotations?.some((r) => r.groups.length)) return false
  if (profile.relatedBuild) return false
  if (profile.lootfilterLinks) return false
  if (profile.pobProfileId) return false

  return true
}

export function stateIsEmpty(state: StoreState, type: ProfileType) {
  if (state.pobBuild !== undefined) {
    return false
  }

  if (state.items == undefined) {
    return true
  }

  if (type === 'atlas-tree' || type === 'passive-tree') {
    if (state.passive_tree != null) {
      if (state.passive_tree.variants.length !== 1 || state.passive_tree.variants[0].history.length) {
        return false
      }
    }
  }
  if (type === 'planner') {
    if (state.planner) {
      if (!profileIsEmpty(state.planner)) return false
    }
  }
  if (type === 'pob') {
    if (state.pobBuild) {
      return false
    }
  }
  if (type === 'embed') {
    if (Object.values(state.items).some(Boolean)) return false
    if (state.embeds.length) return false
  }
  return true
}

export function stateIsValidPublicProfile({
  state,
  type,
  publicOptions
}: {
  state: StoreState
  type: ProfileType
  publicOptions?: PublicOptions
}) {
  if (type !== 'planner' || publicOptions === undefined) return false

  // Can't have no custom items
  if (state.items == undefined) {
    return false
  }

  if (!state.planner) return false

  // Can't have no skills
  if (state.planner.skills.steps.length === 0 || state.planner.skills.steps[0].skills.length === 0) {
    return false
  }

  // Can't have no items
  if (!state.planner.equipment.variants.some((v) => Object.values(v.items).some(Boolean))) {
    return false
  }

  if (publicOptions.category === undefined) return false

  return true
}

export function profileMetadata(profile: Profile) {
  const keystones = new Set<number>()
  try {
    const tree = profile.passives.variants[profile.passives.active]
    const nodes = treeHistoryNodes(
      {
        version: profile.version,
        charClass: profile.class,
        ascendancy: profile.ascendancy
      },
      tree.history
    )
    for (const id of nodes) {
      const node = treeNode(id, profile.version)
      if (!node || !node.ascendancy) continue
      keystones.add(id)
    }
  } catch (e) {
    // do nothing
  }

  const metaData: ProfileMetadata = {
    skills: profile.skills.steps[profile.skills.step]?.skills.map((sk) => sk.gems[0]?.id).filter(notNull) ?? [],
    keystones: [...keystones],
    author: profile.author
  }
  return metaData
}

export function parseMetadata(source: string | undefined, user?: UserEntry): ProfileMetadata | undefined {
  if (!source) return undefined
  try {
    const data = JSON.parse(source) as unknown
    if (!isObject(data)) return undefined
    return {
      skills: keyIs(data, 'skills', isArray)
        ? data.skills.filter((id): id is string => isString(id) && Data.gems[id] != null)
        : [],
      keystones: keyIs(data, 'keystones', isArray) ? data.keystones.filter(isNumber) : [],
      author: 'author' in data ? validateAuthor(data.author, user) : undefined
    }
  } catch (e) {
    return undefined
  }
}

export function stateConvertEmbeds(state: StoreState) {
  if (state.planner) return state
  if (!state.embeds.length && !state.passive_tree) return state
  const result: StoreState = {
    ...Store.initialState,
    id: Store.newId(),
    items: state.items
  }
  let charClass = defaultPlayerClass
  for (const embed of state.embeds) {
    if ('charClass' in embed) {
      charClass = embed.charClass
    }
  }
  if (state.passive_tree && 'charClass' in state.passive_tree) {
    charClass = state.passive_tree.charClass
  }
  result.planner = defaultPlanner(charClass)
  const planner = result.planner
  const defaultEquipment = planner.equipment.variants
  const defaultSkills = planner.skills.steps
  const defaultPassives = planner.passives.variants
  const defaultAtlas = planner.atlas.variants
  for (const embed of [...state.embeds, state.passive_tree]) {
    if (!embed) continue
    switch (embed.type) {
      case 'equipment':
        if (planner.equipment.variants === defaultEquipment) {
          planner.equipment.variants = []
        }
        planner.equipment.variants.push({
          id: getUniqueId(),
          name: embed.name,
          items: embed.items
        })
        break
      case 'skills':
        if (!embed.steps.length) continue
        if (planner.skills.steps === defaultSkills) {
          planner.skills.steps = []
          planner.skills.step = embed.step
        }
        planner.skills.steps.push(...embed.steps)
        break
      case 'passives':
        if (!embed.variants.length) continue
        if (planner.passives.variants === defaultPassives) {
          planner.passives.variants = []
          planner.passives.active = embed.active
        }
        planner.passives.variants.push(...embed.variants)
        break
      case 'atlas':
        if (!embed.variants.length) continue
        if (planner.atlas.variants === defaultAtlas) {
          planner.atlas.variants = []
          planner.atlas.active = embed.active
        }
        planner.atlas.variants.push(...embed.variants)
        planner.atlas.strategy = embed.strategy
        break
      case 'pantheon':
        planner.pantheon = embed.active
        break
    }
  }
  return result
}

export function stateConvertPlanner(state: StoreState) {
  const planner = _.cloneDeep(state?.planner)
  if (!state || !planner) return state
  const result: StoreState = { ...state, embeds: state.embeds ?? [] }

  let index = 0
  for (const embed of planner.equipment.variants) {
    result.embeds.push({ ...embed, type: 'equipment', id: index, name: embed.name || `Equipment ${index}` })
    index += 1
  }

  result.embeds.push({ ...planner.skills, type: 'skills', id: index, name: `Skills` })
  index += 1

  result.embeds.push({
    ...planner.passives,
    type: 'passives',
    id: index,
    name: `Passives`,
    charClass: planner.class,
    version: planner.version
  })
  index += 1

  result.embeds.push({
    ...planner.atlas,
    type: 'atlas',
    id: index,
    name: `Atlas`,
    version: planner.version
  })
  index += 1

  result.embeds.push({ active: planner.pantheon, type: 'pantheon', id: index, name: `Pantheon` })
  index += 1

  return result
}

export function plannerUpgradeVersion(state: StoreState): StoreState {
  if (!state.planner) return state
  if (state.planner.version === Data.defaultVersion) return state

  const passives = validateTreeEmbed(
    {
      id: 0,
      type: 'passives',
      name: '',
      charClass: state.planner.class,
      ascendancy: state.planner.ascendancy,
      version: Data.defaultVersion,
      ...state.planner.passives
    },
    state.items
  )

  const atlas = validateTreeEmbed(
    {
      id: 0,
      type: 'atlas',
      name: '',
      version: Data.defaultVersion,
      ...state.planner.atlas
    },
    state.items
  )

  return {
    ...state,
    planner: {
      ...state.planner,
      version: Data.defaultVersion,
      passives: {
        variants: passives.variants,
        active: passives.active
      },
      atlas: {
        variants: atlas.variants,
        active: atlas.active
      }
    }
  }
}
